/**
 * The native launch splash stays up (`launchAutoHide: false` in lighthouse-mobile's capacitor.config.ts) until
 * the app has something to show: the first route resolved, the theme applied (index.html and useTheme do that
 * before mount) and a frame painted. Then it fades out over 200ms onto a page of the same colour, so launch is
 * one continuous image instead of splash → blank WebView → spinner.
 *
 * The plugin is imported statically on purpose: lighthouse-mobile's build fails if the bundle never registers
 * `SplashScreen` (the splash would otherwise never hide). A safety timer hides it even if boot stalls.
 */
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import type { Router } from 'vue-router'
import { isNativePlatform } from '@/utils/platform'

export const SPLASH_FADE_MS = 200
/** Never hold the splash longer than this, whatever happens during boot. */
export const SPLASH_MAX_MS = 4000

let hidden = false

export async function hideSplash(): Promise<void> {
    if (hidden || !isNativePlatform() || !Capacitor.isPluginAvailable('SplashScreen')) return
    hidden = true
    try {
        await SplashScreen.hide({ fadeOutDuration: SPLASH_FADE_MS })
    } catch {
        // Already hidden (an older shell auto-hid it).
    }
}

function nextPaint(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
}

/** Call once after `app.mount()`. */
export function hideSplashAfterFirstPaint(router: Router): void {
    if (!isNativePlatform()) return
    const timer = setTimeout(() => void hideSplash(), SPLASH_MAX_MS)
    void router
        .isReady()
        .catch(() => {})
        .then(nextPaint)
        .then(() => {
            clearTimeout(timer)
            return hideSplash()
        })
}
